import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import * as api from '../services/api';

// Gate debug logging behind NODE_ENV for development use only
const debugLog = (...args: any[]) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface User {
  username: string;
  email: string;
}

interface LoginResponse {
  token: string;
  user: User;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  adminToken: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adminToken, setAdminToken] = useState<string | null>(null);

  debugLog('[DEBUG AuthContext] AuthProvider rendering', { isAuthenticated, user, loading });

  // Check authentication status on mount
  useEffect(() => {
    checkAuthState();
  }, []);

  /**
   * Check if user is currently authenticated via localStorage
   */
  const checkAuthState = async () => {
    debugLog('[DEBUG AuthContext] Checking auth state...');
    setLoading(true);

    try {
      const storedUser = localStorage.getItem('spitfire_admin_user');
      const storedToken = localStorage.getItem('spitfire_admin_token');

      if (storedUser && storedToken) {
        // Validate token with backend
        debugLog('[DEBUG AuthContext] Validating stored token...');

        try {
          const response = await api.post<ApiResponse<{ valid: boolean; user: User }>>(
            '/auth/validate',
            {},
            { skipAuth: true, headers: { 'Authorization': `Bearer ${storedToken}` } }
          );

          if (response.success && response.data.valid) {
            const userData = JSON.parse(storedUser);
            setUser(userData);
            setAdminToken(storedToken);
            setIsAuthenticated(true);
            debugLog('[DEBUG AuthContext] Token validated, user restored:', userData.email);
          } else {
            // Token invalid, clear storage
            debugLog('[DEBUG AuthContext] Token validation failed, clearing storage');
            localStorage.removeItem('spitfire_admin_user');
            localStorage.removeItem('spitfire_admin_token');
            setUser(null);
            setAdminToken(null);
            setIsAuthenticated(false);
          }
        } catch (validationError) {
          // Token validation failed (possibly expired or invalid)
          debugLog('[DEBUG AuthContext] Token validation error, clearing storage:', validationError);
          localStorage.removeItem('spitfire_admin_user');
          localStorage.removeItem('spitfire_admin_token');
          setUser(null);
          setAdminToken(null);
          setIsAuthenticated(false);
        }
      } else {
        debugLog('[DEBUG AuthContext] No stored auth found');
        setUser(null);
        setAdminToken(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      debugLog('[DEBUG AuthContext] Error checking auth state:', err);
      setUser(null);
      setAdminToken(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      debugLog('[DEBUG AuthContext] Auth state check complete');
    }
  };

  /**
   * Sign in with email and password via backend API
   */
  const login = async (email: string, password: string) => {
    debugLog('[DEBUG AuthContext] Login attempt for:', email);
    setLoading(true);
    setError(null);

    try {
      // Call backend auth endpoint
      const response = await api.post<ApiResponse<LoginResponse>>(
        '/auth/login',
        { email, password },
        { skipAuth: true } // Don't send auth header for login
      );

      if (!response.success) {
        throw new Error(response.message || 'Login failed');
      }

      const { token, user: userData } = response.data;

      // Store in localStorage
      localStorage.setItem('spitfire_admin_user', JSON.stringify(userData));
      localStorage.setItem('spitfire_admin_token', token);

      // Update state
      setUser(userData);
      setAdminToken(token);
      setIsAuthenticated(true);

      debugLog('[DEBUG AuthContext] Login successful for:', email);
    } catch (err: any) {
      console.error('[DEBUG AuthContext] Login error:', err);

      // Extract error message
      let errorMessage = 'Login failed';
      if (err.data?.message) {
        errorMessage = err.data.message;
      } else if (err.data?.error) {
        errorMessage = err.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
      setAdminToken(null);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign out and clear authentication state
   */
  const logout = async () => {
    debugLog('[DEBUG AuthContext] Logout initiated');
    setLoading(true);
    setError(null);

    try {
      // Clear localStorage
      localStorage.removeItem('spitfire_admin_user');
      localStorage.removeItem('spitfire_admin_token');

      // Clear state
      setUser(null);
      setAdminToken(null);
      setIsAuthenticated(false);

      debugLog('[DEBUG AuthContext] User logged out successfully');
    } catch (err) {
      console.error('[DEBUG AuthContext] Logout error:', err);
      // Even if logout fails, clear local state
      setUser(null);
      setAdminToken(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Clear error message
   */
  const clearError = () => {
    debugLog('[DEBUG AuthContext] Clearing error');
    setError(null);
  };

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    error,
    adminToken,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to access auth context
 * Must be used within AuthProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
